import { useQueryClient as useInvoiceRevisionQueryClient } from '@tanstack/react-query';
import { InvoiceRevisionPanel } from '../InvoiceRevisionPanel/InvoiceRevisionPanel';
import { Fragment, useEffect, useMemo, useState } from 'react';

import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Mail,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  X,
} from 'lucide-react';

import { isAxiosError } from 'axios';

import { toast } from 'react-toastify';

import { useQuery } from '@tanstack/react-query';

import { useCreateOrderInvoice, useInvoicePreview } from '../../features/invoicing/api';
// BMP_QBO_INVOICE_PDF_DIALOG_IMPORT_V1
import { useInvoicePdfStatus, useSyncInvoicePdf } from '../../features/invoicing/invoicePdfApi';

import { getBlumeInvoiceSettings } from '../../features/integrations/blume/api';

import type {
  BlumeInvoiceServiceMapping,
  BlumeInvoiceServiceOption,
} from '../../features/integrations/blume/api';

import type {
  CreateOrderInvoiceResponse,
  InvoiceAddress,
  InvoiceDraft,
  InvoiceIssue,
} from '../../features/invoicing/types';

import { InvoiceDocumentPicker } from '../InvoiceDocumentPicker/InvoiceDocumentPicker';
import { attachOrderInvoiceDocument } from '../../features/invoicing/invoiceAttachmentsApi';
import { SearchableQuickBooksServiceSelect } from './SearchableQuickBooksServiceSelect';
import { getInvoiceAccountingCatalog } from '../../features/invoicing/accountingApi';
import { matchShipToStateToLocationId } from './invoiceAccountingLocationMatch';
import './invoice-accounting.scss';
import './InvoiceDialog.scss';
import './searchable-qbo-service.scss';

interface InvoiceDialogProps {
  isOpen: boolean;

  orderIds: string[];

  onClose: () => void;

  onInvoiceCreated?: (
    orderId: string,

    result: CreateOrderInvoiceResponse
  ) => void;
}

interface InvoiceAdditionalServiceEdit {
  id: string;

  quickBooksItemId: string;

  description: string;

  quantity: string;

  unitPrice: string;
}

interface InvoiceDraftEdits {
  invoiceDate: string;

  dueDate: string;

  customerMemo: string;

  privateNote: string;

  classId: string;
  locationId: string;
  additionalServiceLines: InvoiceAdditionalServiceEdit[];
}

let additionalServiceLineSequence = 0;

function createAdditionalServiceLineId() {
  additionalServiceLineSequence += 1;

  return `invoice-service-${Date.now()}-${additionalServiceLineSequence}`;
}

function centsToDollarInput(cents: number | null | undefined) {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return '';
  }

  return (cents / 100).toFixed(2);
}

function dollarsInputToCents(value: string): number | null {
  const normalized = value.trim();

  if (!normalized) {
    return null;
  }

  const dollars = Number(normalized);

  if (!Number.isFinite(dollars) || dollars < 0) {
    return null;
  }

  return Math.round(dollars * 100);
}

function quantityInputToNumber(value: string): number | null {
  const normalized = value.trim();

  if (!normalized) {
    return null;
  }

  const quantity = Number(normalized);

  if (!Number.isFinite(quantity) || quantity <= 0) {
    return null;
  }

  return quantity;
}

function additionalServiceAmountCents(line: InvoiceAdditionalServiceEdit): number | null {
  const quantity = quantityInputToNumber(line.quantity);

  const unitPriceCents = dollarsInputToCents(line.unitPrice);

  if (quantity === null || unitPriceCents === null) {
    return null;
  }

  return Math.round(quantity * unitPriceCents);
}

function serviceLabel(option: BlumeInvoiceServiceOption) {
  return option.fullyQualifiedName ?? option.name;
}

function defaultRateForService(
  option: BlumeInvoiceServiceOption | undefined,

  configuredMapping: BlumeInvoiceServiceMapping | null | undefined
) {
  if (
    configuredMapping?.defaultUnitPriceCents !== null &&
    configuredMapping?.defaultUnitPriceCents !== undefined
  ) {
    return centsToDollarInput(configuredMapping.defaultUnitPriceCents);
  }

  if (typeof option?.unitPrice === 'number' && Number.isFinite(option.unitPrice)) {
    return option.unitPrice.toFixed(2);
  }

  return '';
}

function formatCents(cents: number | null | undefined) {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',

    currency: 'USD',

    minimumFractionDigits: 2,

    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatQuantity(value: number) {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 3,
  }).format(value);
}

function formatAddress(address: InvoiceAddress | null) {
  if (!address) {
    return '—';
  }

  const cityState = [address.city, address.state].filter(Boolean).join(', ');

  const cityStateZip = [cityState, address.postalCode].filter(Boolean).join(' ');

  return [address.name, address.line1, address.line2, cityStateZip, address.country]
    .filter(Boolean)
    .join('\n');
}

type ExistingInvoiceStatus = NonNullable<InvoiceDraft['existingInvoice']>['status'];

function statusLabel(status: ExistingInvoiceStatus) {
  switch (status) {
    case 'CREATING':
      return 'Creating';

    case 'CREATED':
      return 'Created in QBO';

    case 'SENT':
      return 'Sent';

    case 'SEND_FAILED':
      return 'Send failed';

    case 'FAILED':
      return 'Failed';

    default:
      return status;
  }
}

function defaultEdits(draft: InvoiceDraft): InvoiceDraftEdits {
  return {
    invoiceDate: draft.header.invoiceDate,

    dueDate: draft.header.dueDate,

    // The BMP PO number is sent through its configured QuickBooks custom field.
    // Never prefill a customer-facing invoice note with the PO.
    customerMemo: '',

    privateNote: '',
    classId: '',
    locationId: '',

    additionalServiceLines: [],
  };
}

function getErrorPayload(error: unknown) {
  if (!isAxiosError(error) || !error.response?.data || typeof error.response.data !== 'object') {
    return null;
  }

  return error.response.data as {
    error?: string;

    message?: string;

    partialSuccess?: boolean;

    createdInvoice?: {
      quickBooksInvoiceId?: string;

      docNumber?: string | null;
    } | null;
  };
}

function IssueList({
  issues,
  severity,
}: {
  issues: InvoiceIssue[];

  severity: InvoiceIssue['severity'];
}) {
  const filtered = issues.filter((issue) => issue.severity === severity);

  if (filtered.length === 0) {
    return null;
  }

  if (severity === 'WARNING') {
    return (
      <details className="invoice-dialog__warnings">
        <summary>
          {filtered.length} review {filtered.length === 1 ? 'note' : 'notes'}
        </summary>

        <ul>
          {filtered.map((issue) => (
            <li key={issue.code}>{issue.message}</li>
          ))}
        </ul>
      </details>
    );
  }

  return (
    <div className="invoice-dialog__blockers">
      {filtered.map((issue) => (
        <div className="invoice-dialog__blocker" key={issue.code}>
          <AlertTriangle size={16} />

          <span>{issue.message}</span>
        </div>
      ))}
    </div>
  );
}

export function InvoiceDialog({ isOpen, orderIds, onClose, onInvoiceCreated }: InvoiceDialogProps) {
  const normalizedOrderIds = useMemo(
    () => Array.from(new Set(orderIds.map((orderId) => orderId.trim()).filter(Boolean))),
    [orderIds]
  );

  const previewQuery = useInvoicePreview(normalizedOrderIds, isOpen);
  const accountingQuery = useQuery({
    // Unique order IDs prevent reuse of another organization's catalog.
    queryKey: ['qbo-invoice-accounting', ...normalizedOrderIds],
    queryFn: getInvoiceAccountingCatalog,
    enabled: isOpen,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const invoiceSettingsQuery = useQuery({
    queryKey: ['blume-invoice-settings'],

    queryFn: getBlumeInvoiceSettings,

    enabled: isOpen,

    staleTime: 30_000,

    refetchOnWindowFocus: false,
  });

  // Treat each opening as a fresh invoice review. This dialog often stays mounted
  // with isOpen=false, so ordinary React Query staleTime=0 can briefly render
  // cached totals from the previous opening.
  const invoiceCacheClient = useInvoiceRevisionQueryClient();
  useEffect(() => {
    if (isOpen) return;
    invoiceCacheClient.removeQueries({ queryKey: ['org:invoice-preview'] });
    invoiceCacheClient.removeQueries({ queryKey: ['org:invoice-revision'] });
  }, [isOpen, invoiceCacheClient]);
  useEffect(
    () => () => {
      // Also clean up when the parent unmounts the dialog on close.
      invoiceCacheClient.removeQueries({ queryKey: ['org:invoice-preview'] });
      invoiceCacheClient.removeQueries({ queryKey: ['org:invoice-revision'] });
    },
    [invoiceCacheClient]
  );

  const createMutation = useCreateOrderInvoice();
  const [isAttachingAfterCreate, setIsAttachingAfterCreate] = useState(false);
  const [selectedDocumentsByOrder, setSelectedDocumentsByOrder] = useState<
    Record<string, string[]>
  >({});
  useEffect(() => {
    if (!isOpen) setSelectedDocumentsByOrder({});
  }, [isOpen]);

  const [activeIndex, setActiveIndex] = useState(0);

  const [editsByOrderId, setEditsByOrderId] = useState<Record<string, InvoiceDraftEdits>>({});
  // A user explicitly picking Unassigned must not be overwritten by auto-match.
  const [locationTouchedByOrderId, setLocationTouchedByOrderId] = useState<Record<string, boolean>>(
    {}
  );
  const [revisionDateEditsByOrderId, setRevisionDateEditsByOrderId] = useState<
    Record<string, { invoiceDate: string; dueDate: string }>
  >({});

  const drafts = useMemo(() => previewQuery.data?.invoices ?? [], [previewQuery.data?.invoices]);

  const activeDraft = drafts[activeIndex] ?? null;
  // BMP_QBO_INVOICE_PDF_DIALOG_HOOK_V1
  const pdfStatus = useInvoicePdfStatus(
    activeDraft?.orderId,
    Boolean(isOpen && activeDraft?.existingInvoice?.quickBooksInvoiceId)
  );
  const syncPdf = useSyncInvoicePdf();

  const serviceOptions = useMemo(
    () => invoiceSettingsQuery.data?.serviceOptions ?? [],
    [invoiceSettingsQuery.data?.serviceOptions]
  );

  const serviceOptionById = useMemo(
    () => new Map(serviceOptions.map((option) => [option.quickBooksItemId, option])),
    [serviceOptions]
  );

  const configuredFuelService = invoiceSettingsQuery.data?.settings.fuelSurcharge ?? null;

  const configuredDirectShipService = invoiceSettingsQuery.data?.settings.directShipFee ?? null;

  const configuredHighwayService = invoiceSettingsQuery.data?.settings.highwayUseFee ?? null;

  const selectableServiceOptions = useMemo(
    () =>
      serviceOptions.filter(
        (service) =>
          service.quickBooksItemId !== configuredFuelService?.quickBooksItemId &&
          service.quickBooksItemId !== configuredDirectShipService?.quickBooksItemId
      ),
    [
      configuredFuelService?.quickBooksItemId,
      configuredDirectShipService?.quickBooksItemId,
      serviceOptions,
    ]
  );

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(0);

      setEditsByOrderId({});
      setLocationTouchedByOrderId({});
      setRevisionDateEditsByOrderId({});

      return;
    }

    if (activeIndex >= drafts.length && drafts.length > 0) {
      setActiveIndex(drafts.length - 1);
    }
  }, [activeIndex, drafts.length, isOpen]);

  useEffect(() => {
    if (drafts.length === 0) {
      return;
    }

    setEditsByOrderId((current) => {
      const next = {
        ...current,
      };

      for (const draft of drafts) {
        if (!next[draft.orderId]) {
          next[draft.orderId] = defaultEdits(draft);
        }
      }

      return next;
    });
  }, [drafts]);

  const activeEdits = activeDraft
    ? (editsByOrderId[activeDraft.orderId] ?? defaultEdits(activeDraft))
    : null;

  // An existing invoice is read back from QuickBooks, not recomputed from old BMP snapshots.
  const postedQuickBooksLines = activeDraft?.postedQuickBooksLines ?? null;
  const isPostedQuickBooksInvoice = Boolean(activeDraft?.existingInvoice?.quickBooksInvoiceId);
  // Catalog can arrive after drafts. Derive the suggestion from current data rather
  // than initializing once, and never overwrite a manual choice (including blank).
  const suggestedLocationId =
    !isPostedQuickBooksInvoice && accountingQuery.data?.locationEnabled
      ? matchShipToStateToLocationId(
          activeDraft?.header.shipTo?.state,
          accountingQuery.data.locations
        )
      : '';
  const activeLocationId =
    activeDraft && activeEdits
      ? locationTouchedByOrderId[activeDraft.orderId]
        ? activeEdits.locationId
        : suggestedLocationId
      : '';

  const activeAdditionalServiceTotalCents = activeEdits
    ? activeEdits.additionalServiceLines.reduce(
        (total, line) => total + (additionalServiceAmountCents(line) ?? 0),
        0
      )
    : 0;

  const hasInvalidAdditionalServiceLines =
    !isPostedQuickBooksInvoice &&
    Boolean(
      activeEdits?.additionalServiceLines.some(
        (line) =>
          !line.quickBooksItemId ||
          quantityInputToNumber(line.quantity) === null ||
          dollarsInputToCents(line.unitPrice) === null
      )
    );

  const postedInvoiceBaseTotalCents = isPostedQuickBooksInvoice
    ? (activeDraft?.postedQuickBooksTotalAmountCents ?? activeDraft?.totalAmountCents ?? null)
    : (activeDraft?.totalAmountCents ?? null);

  const displayedInvoiceTotalCents =
    postedInvoiceBaseTotalCents === null
      ? null
      : postedInvoiceBaseTotalCents +
        (isPostedQuickBooksInvoice ? 0 : activeAdditionalServiceTotalCents);

  const activeDraftHasFuelSurcharge = Boolean(
    activeDraft?.lines.some(
      (line) => (line.fuelSurchargeCents ?? 0) > 0 || Boolean(line.automaticFuelLine)
    )
  );

  const fuelSurchargeServiceConfigured = Boolean(configuredFuelService?.quickBooksItemId);

  const showFuelConfigurationWarning = Boolean(
    activeDraft &&
    !activeDraft.existingInvoice?.quickBooksInvoiceId &&
    activeDraftHasFuelSurcharge &&
    !fuelSurchargeServiceConfigured &&
    !invoiceSettingsQuery.isLoading
  );

  if (!isOpen) {
    return null;
  }

  const updateActiveEdits = (patch: Partial<InvoiceDraftEdits>) => {
    if (!activeDraft) {
      return;
    }

    setEditsByOrderId((current) => ({
      ...current,

      [activeDraft.orderId]: {
        ...defaultEdits(activeDraft),

        ...current[activeDraft.orderId],

        ...patch,
      },
    }));
  };

  const suggestedHighwayQuantity = () => {
    if (!activeDraft || activeDraft.lines.length === 0) {
      return '1';
    }

    const tonnageLines = activeDraft.lines.filter((line) => /ton/i.test(line.unitName ?? ''));

    if (tonnageLines.length !== activeDraft.lines.length) {
      return '1';
    }

    const quantity = tonnageLines.reduce(
      (total, line) => total + (Number.isFinite(line.bmpQuantity) ? line.bmpQuantity : 0),
      0
    );

    return quantity > 0 ? String(quantity) : '1';
  };

  const configuredMappingForService = (quickBooksItemId: string) => {
    if (configuredHighwayService?.quickBooksItemId === quickBooksItemId) {
      return configuredHighwayService;
    }

    return null;
  };

  const addServiceLine = (configuredMapping?: BlumeInvoiceServiceMapping | null) => {
    if (!activeDraft || !activeEdits || activeDraft.existingInvoice?.quickBooksInvoiceId) {
      return;
    }

    if (configuredMapping?.kind === 'FUEL_SURCHARGE') {
      return;
    }

    const quickBooksItemId = configuredMapping?.quickBooksItemId ?? '';

    const option = quickBooksItemId ? serviceOptionById.get(quickBooksItemId) : undefined;

    const quantity =
      configuredMapping?.kind === 'HIGHWAY_USE_FEE' ? suggestedHighwayQuantity() : '1';

    updateActiveEdits({
      additionalServiceLines: [
        ...activeEdits.additionalServiceLines,

        {
          id: createAdditionalServiceLineId(),

          quickBooksItemId,

          description: option?.description ?? '',

          quantity,

          unitPrice: defaultRateForService(option, configuredMapping),
        },
      ],
    });
  };

  const updateServiceLine = (
    lineId: string,

    patch: Partial<InvoiceAdditionalServiceEdit>
  ) => {
    if (!activeEdits) {
      return;
    }

    updateActiveEdits({
      additionalServiceLines: activeEdits.additionalServiceLines.map((line) =>
        line.id === lineId
          ? {
              ...line,

              ...patch,
            }
          : line
      ),
    });
  };

  const selectServiceForLine = (
    line: InvoiceAdditionalServiceEdit,

    quickBooksItemId: string
  ) => {
    const option = serviceOptionById.get(quickBooksItemId);

    const configuredMapping = configuredMappingForService(quickBooksItemId);

    const shouldSuggestHighwayQuantity =
      !line.quickBooksItemId && configuredMapping?.kind === 'HIGHWAY_USE_FEE';

    updateServiceLine(line.id, {
      quickBooksItemId,

      description: option?.description ?? '',

      unitPrice: defaultRateForService(option, configuredMapping),

      quantity: shouldSuggestHighwayQuantity ? suggestedHighwayQuantity() : line.quantity,
    });
  };

  const removeServiceLine = (lineId: string) => {
    if (!activeEdits) {
      return;
    }

    updateActiveEdits({
      additionalServiceLines: activeEdits.additionalServiceLines.filter(
        (line) => line.id !== lineId
      ),
    });
  };

  const handleCreate = async (mode: 'CREATE' | 'CREATE_AND_SEND') => {
    const attachmentIds = activeDraft ? (selectedDocumentsByOrder[activeDraft.orderId] ?? []) : [];
    // BMP_EMAIL_SELECTED_ORDER_ATTACHMENTS_V3
    // QBO only includes attachments in a customer email when they are linked
    // before the Invoice/send request. A successful create is never repeated.
    const sendWithSelectedDocuments = mode === 'CREATE_AND_SEND' && attachmentIds.length > 0;
    const initialInvoiceMode = sendWithSelectedDocuments ? 'CREATE' : mode;

    if (!activeDraft) {
      return;
    }

    const edits = editsByOrderId[activeDraft.orderId] ?? defaultEdits(activeDraft);

    let additionalServiceLines: Array<{
      quickBooksItemId: string;

      description: string | null;

      quantity: number;

      unitPriceCents: number;
    }> = [];

    if (!activeDraft.existingInvoice?.quickBooksInvoiceId) {
      for (let index = 0; index < edits.additionalServiceLines.length; index += 1) {
        const line = edits.additionalServiceLines[index];

        const quantity = quantityInputToNumber(line.quantity);

        const unitPriceCents = dollarsInputToCents(line.unitPrice);

        if (!line.quickBooksItemId || quantity === null || unitPriceCents === null) {
          toast.error(`Complete service line ${index + 1} before creating the invoice.`);

          return;
        }

        additionalServiceLines.push({
          quickBooksItemId: line.quickBooksItemId,

          description: line.description.trim() || null,

          quantity,

          unitPriceCents,
        });
      }
    }

    let emailWithFilesReady = false;

    try {
      let result = await createMutation.mutateAsync({
        orderId: activeDraft.orderId,

        mode: initialInvoiceMode,

        txnDate: edits.invoiceDate || null,

        dueDate: edits.dueDate || null,

        customerMemo: edits.customerMemo.trim() || null,

        privateNote: edits.privateNote.trim() || null,

        additionalServiceLines,
        // Organization-scoped, validated against the live QBO catalog by Blume.
        classId: edits.classId || null,
        locationId:
          (locationTouchedByOrderId[activeDraft.orderId]
            ? edits.locationId
            : accountingQuery.data?.locationEnabled
              ? matchShipToStateToLocationId(
                  activeDraft.header.shipTo?.state,
                  accountingQuery.data.locations
                )
              : '') || null,
      });

      // Attach selected files before invoking the existing-invoice send path.
      // Includes CREATE-only uploads, which can be emailed separately later.
      if (attachmentIds.length && result.invoice.quickBooksInvoiceId) {
        setIsAttachingAfterCreate(true);
        const failed: string[] = [];
        let attachedCount = 0;
        for (const documentId of attachmentIds) {
          try {
            await attachOrderInvoiceDocument(activeDraft.orderId, documentId);
            attachedCount += 1;
          } catch (error: any) {
            failed.push(documentId);
            const message =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Could not confirm attachment';
            toast.error(`A document could not be attached: ${message}`, { autoClose: false });
          }
        }
        setSelectedDocumentsByOrder((previous) => ({ ...previous, [activeDraft.orderId]: failed }));
        if (attachedCount) {
          toast.success(
            `${attachedCount} document${attachedCount === 1 ? '' : 's'} attached to QuickBooks for emailing.`
          );
        }
        if (failed.length) {
          // Do not send a potentially incomplete email. The invoice was already
          // created or reused; leave it linked in BMP for a safe retry.
          onInvoiceCreated?.(activeDraft.orderId, result);
          setIsAttachingAfterCreate(false);
          await previewQuery.refetch();
          toast.warn(
            `Invoice saved, but ${failed.length} selected document(s) were not confirmed. Email was NOT sent. Check the attachments in QuickBooks before retrying.`,
            { autoClose: false }
          );
          return;
        }
      }

      if (sendWithSelectedDocuments) {
        emailWithFilesReady = true;
        // This route is idempotent for invoice creation: it sends the existing
        // linked QBO invoice rather than creating a second invoice.
        onInvoiceCreated?.(activeDraft.orderId, result);
        result = await createMutation.mutateAsync({
          orderId: activeDraft.orderId,
          mode: 'CREATE_AND_SEND',
        });
        if (result.invoice.status !== 'SENT') {
          setIsAttachingAfterCreate(false);
          await previewQuery.refetch();
          toast.warn(
            'QuickBooks has not confirmed the invoice email as sent. Review its email status before trying again.',
            { autoClose: false }
          );
          return;
        }
      }
      setIsAttachingAfterCreate(false);

      onInvoiceCreated?.(activeDraft.orderId, result);

      if (result.invoice.status === 'SENT') {
        toast.success(
          `QuickBooks invoice ${result.invoice.quickBooksDocNumber ?? result.invoice.quickBooksInvoiceId ?? ''} sent successfully.`.trim()
        );
      } else if (result.alreadyExisted) {
        toast.success('Existing QuickBooks invoice found and reused.');
      } else {
        toast.success('QuickBooks invoice created successfully.');
      }

      // Clear the now-posted manual services from the local draft.
      // QuickBooks already includes them in the authoritative total.
      if (result.invoice.quickBooksInvoiceId) {
        setEditsByOrderId((current) => {
          const existingEdits = current[activeDraft.orderId];
          if (!existingEdits?.additionalServiceLines.length) return current;
          return {
            ...current,
            [activeDraft.orderId]: {
              ...existingEdits,
              additionalServiceLines: [],
            },
          };
        });
      }

      await previewQuery.refetch();
    } catch (error: unknown) {
      setIsAttachingAfterCreate(false);

      if (emailWithFilesReady) {
        toast.error(
          'The QuickBooks invoice and selected attachments were saved, but the email result could not be confirmed. Check QuickBooks email status before retrying Send existing invoice.',
          { autoClose: false }
        );
        await previewQuery.refetch();
        return;
      }

      const payload = getErrorPayload(error);

      if (payload?.partialSuccess && payload.createdInvoice) {
        toast.warn(
          `QuickBooks invoice ${payload.createdInvoice.docNumber ?? payload.createdInvoice.quickBooksInvoiceId ?? ''} was created, but the remaining action failed. BMP saved the existing invoice so it can be retried safely.`.trim(),
          {
            autoClose: false,
          }
        );

        await previewQuery.refetch();

        return;
      }

      toast.error(
        payload?.message ??
          (error instanceof Error ? error.message : 'Unable to create the QuickBooks invoice.')
      );
    }
  };

  return (
    <div
      className="modal invoice-dialog-shell"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invoice-dialog-title"
    >
      <div
        className="modal__backdrop"
        onClick={createMutation.isPending || isAttachingAfterCreate ? undefined : onClose}
      />

      <div className="invoice-dialog">
        <header className="invoice-dialog__header">
          <div>
            <div className="invoice-dialog__eyebrow">QuickBooks</div>

            <h2 id="invoice-dialog-title">Invoice review</h2>

            <p>Review the saved BMP order values before creating the QuickBooks invoice.</p>
          </div>

          <div className="invoice-dialog__header-actions">
            {drafts.length > 1 && (
              <div className="invoice-dialog__pager">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={activeIndex === 0 || createMutation.isPending}
                  onClick={() => setActiveIndex((current) => Math.max(0, current - 1))}
                >
                  <ChevronLeft size={14} />
                </button>

                <span>
                  {activeIndex + 1} of {drafts.length}
                </span>

                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={activeIndex >= drafts.length - 1 || createMutation.isPending}
                  onClick={() =>
                    setActiveIndex((current) => Math.min(drafts.length - 1, current + 1))
                  }
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}

            <button
              type="button"
              className="invoice-dialog__close"
              onClick={onClose}
              disabled={createMutation.isPending || isAttachingAfterCreate}
              aria-label="Close invoice review"
            >
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="invoice-dialog__body">
          {previewQuery.isLoading && (
            <div className="invoice-dialog__loading">
              <RefreshCw className="invoice-dialog__spin" size={20} />
              Preparing invoice from saved BMP data…
            </div>
          )}

          {previewQuery.error && (
            <div className="invoice-dialog__error">
              <AlertTriangle size={18} />

              <div>
                <strong>Unable to prepare invoice</strong>

                <span>
                  {previewQuery.error instanceof Error
                    ? previewQuery.error.message
                    : 'Invoice preview failed.'}
                </span>
              </div>
            </div>
          )}

          {activeDraft && activeEdits && (
            <>
              <InvoiceDocumentPicker
                key={activeDraft.orderId}
                orderId={activeDraft.orderId}
                quickBooksInvoiceId={activeDraft.existingInvoice?.quickBooksInvoiceId}
                selectedIds={selectedDocumentsByOrder[activeDraft.orderId] ?? []}
                onSelectedIdsChange={(ids) =>
                  setSelectedDocumentsByOrder((prev) => ({ ...prev, [activeDraft.orderId]: ids }))
                }
              />

              <div className="invoice-dialog__summary-row">
                <div>
                  <span className="invoice-dialog__summary-label">BMP load</span>

                  <strong>{activeDraft.loadLabel}</strong>
                </div>

                <div>
                  <span className="invoice-dialog__summary-label">Customer</span>

                  <strong>{activeDraft.customer.bmpName ?? '—'}</strong>
                </div>

                <div>
                  <span className="invoice-dialog__summary-label">Invoice total</span>

                  <strong>{formatCents(displayedInvoiceTotalCents)}</strong>
                </div>

                {activeDraft.existingInvoice && (
                  <div>
                    <span className="invoice-dialog__summary-label">QBO status</span>

                    <span
                      className={`invoice-dialog__status invoice-dialog__status--${activeDraft.existingInvoice.status.toLowerCase()}`}
                    >
                      {statusLabel(activeDraft.existingInvoice.status)}
                    </span>
                  </div>
                )}
              </div>

              {activeDraft.existingInvoice?.quickBooksDocNumber && (
                <div className="invoice-dialog__existing">
                  <CheckCircle2 size={17} />

                  <span>
                    QuickBooks invoice{' '}
                    <strong>#{activeDraft.existingInvoice.quickBooksDocNumber}</strong> is already
                    linked to this BMP order.
                  </span>
                </div>
              )}

              {/* BMP_QBO_INVOICE_PDF_DIALOG_BUTTON_V1 */}
              {activeDraft.existingInvoice?.quickBooksInvoiceId && (
                <div className="invoice-dialog__existing">
                  <FileText size={17} />
                  <span>
                    {pdfStatus.data?.status === 'READY'
                      ? `Invoice PDF saved in Order Documents: ${pdfStatus.data.fileName ?? ''}`
                      : pdfStatus.data?.status === 'SYNCING'
                        ? 'Invoice PDF is syncing…'
                        : 'Invoice PDF is not yet saved in Order Documents.'}
                    {pdfStatus.data?.status === 'FAILED' && pdfStatus.data.lastError && (
                      <small> {pdfStatus.data.lastError}</small>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn btn--secondary"
                    disabled={syncPdf.isPending || pdfStatus.data?.status === 'SYNCING'}
                    onClick={async () => {
                      try {
                        await syncPdf.mutateAsync({ orderId: activeDraft.orderId, force: true });
                        toast.success('QuickBooks invoice PDF saved in Order Documents.');
                      } catch (error: any) {
                        toast.error(
                          error?.response?.data?.message ||
                            error?.message ||
                            'Could not sync invoice PDF.'
                        );
                      }
                    }}
                  >
                    <RefreshCw size={15} /> {syncPdf.isPending ? 'Syncing…' : 'Sync invoice PDF'}
                  </button>
                </div>
              )}

              <IssueList issues={activeDraft.issues} severity="BLOCKER" />

              <section className="invoice-dialog__section">
                <div className="invoice-dialog__section-heading">
                  <div>
                    <h3>Customer & addresses</h3>

                    <p>BMP values mapped to the QuickBooks invoice header.</p>
                  </div>
                </div>

                <div className="invoice-dialog__grid invoice-dialog__customer-summary-grid">
                  <div className="invoice-field">
                    <label>Customer</label>

                    <div className="invoice-field__read-only">
                      <strong>
                        {activeDraft.customer.quickBooksName ?? activeDraft.customer.bmpName ?? '—'}
                      </strong>

                      {activeDraft.customer.quickBooksCustomerId && (
                        <small>QBO ID {activeDraft.customer.quickBooksCustomerId}</small>
                      )}
                    </div>
                  </div>

                  <div className="invoice-field">
                    <label>PO number</label>

                    <div className="invoice-field__read-only">
                      {activeDraft.header.poNumber ?? '—'}
                    </div>
                  </div>
                </div>

                <div className="invoice-dialog__address-grid">
                  <div className="invoice-address-card">
                    <div className="invoice-address-card__title">Ship to</div>

                    <pre>{formatAddress(activeDraft.header.shipTo)}</pre>
                  </div>

                  <div className="invoice-address-card">
                    <div className="invoice-address-card__title">Ship from</div>

                    <pre>{formatAddress(activeDraft.header.shipFrom)}</pre>
                  </div>
                </div>
              </section>

              <section className="invoice-dialog__section">
                <div className="invoice-dialog__section-heading">
                  <div>
                    <h3>Shipping & invoice fields</h3>

                    <p>
                      This follows the field mapping in the current QuickBooks invoice workflow.
                    </p>
                  </div>
                </div>

                <div className="invoice-dialog__grid invoice-dialog__grid--4">
                  <div className="invoice-field">
                    <label>Ship via</label>

                    <div className="invoice-field__read-only">
                      {activeDraft.header.shipVia ?? '—'}
                    </div>
                  </div>

                  <div className="invoice-field">
                    <label>Shipping date</label>

                    <div className="invoice-field__read-only">
                      {activeDraft.header.shippingDate ?? '—'}
                    </div>
                  </div>

                  <div className="invoice-field">
                    <label>Tracking #</label>

                    <div className="invoice-field__read-only">
                      {activeDraft.header.trackingNumber ?? '—'}
                    </div>
                  </div>

                  <div className="invoice-field">
                    <label>DeFeo Ticket #</label>

                    <div className="invoice-field__read-only">
                      {activeDraft.header.ticketNumber ?? '—'}
                    </div>
                  </div>

                  {!isPostedQuickBooksInvoice && (
                    <>
                      {accountingQuery.data?.classEnabled && (
                        <div className="invoice-field invoice-accounting-field">
                          <label htmlFor={`qbo-class-${activeDraft.orderId}`}>
                            QuickBooks Class
                          </label>
                          <SearchableQuickBooksServiceSelect
                            id={`qbo-class-${activeDraft.orderId}`}
                            ariaLabel="QuickBooks Class"
                            emptyLabel="Unassigned"
                            searchPlaceholder="Search QuickBooks classes…"
                            noMatchesLabel="No matching QuickBooks classes"
                            value={activeEdits.classId}
                            onChange={(classId) => updateActiveEdits({ classId })}
                            disabled={createMutation.isPending}
                            options={accountingQuery.data.classes.map((option) => ({
                              value: option.id,
                              label: option.name,
                            }))}
                          />
                        </div>
                      )}
                      {accountingQuery.data?.locationEnabled && (
                        <div className="invoice-field invoice-accounting-field">
                          <label htmlFor={`qbo-location-${activeDraft.orderId}`}>
                            QuickBooks Location
                          </label>
                          <SearchableQuickBooksServiceSelect
                            id={`qbo-location-${activeDraft.orderId}`}
                            ariaLabel="QuickBooks Location"
                            emptyLabel="Unassigned"
                            searchPlaceholder="Search QuickBooks locations…"
                            noMatchesLabel="No matching QuickBooks locations"
                            value={activeLocationId}
                            onChange={(locationId) => {
                              setLocationTouchedByOrderId((previous) => ({
                                ...previous,
                                [activeDraft.orderId]: true,
                              }));
                              updateActiveEdits({ locationId });
                            }}
                            disabled={createMutation.isPending}
                            options={accountingQuery.data.locations.map((option) => ({
                              value: option.id,
                              label: option.name,
                            }))}
                          />
                          {!locationTouchedByOrderId[activeDraft.orderId] &&
                            suggestedLocationId && (
                              <small className="invoice-accounting-field__hint">
                                Suggested from ship-to state: {activeDraft.header.shipTo?.state}
                              </small>
                            )}
                        </div>
                      )}
                      {accountingQuery.isError && (
                        <div className="invoice-field">
                          <small>
                            QuickBooks Classes/Locations could not be loaded. No classification will
                            be assigned.
                          </small>
                          <button type="button" onClick={() => accountingQuery.refetch()}>
                            Retry
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  <div className="invoice-field">
                    <label>Terms</label>

                    <div className="invoice-field__read-only">{activeDraft.header.termsLabel}</div>
                  </div>

                  <div className="invoice-field">
                    <label htmlFor={`invoice-date-${activeDraft.orderId}`}>Invoice date</label>

                    <input
                      id={`invoice-date-${activeDraft.orderId}`}
                      type="date"
                      value={
                        activeDraft.existingInvoice?.quickBooksInvoiceId
                          ? (revisionDateEditsByOrderId[activeDraft.orderId]?.invoiceDate ?? '')
                          : activeEdits.invoiceDate
                      }
                      onChange={(event) => {
                        if (activeDraft.existingInvoice?.quickBooksInvoiceId) {
                          const nextValue = event.target.value;
                          setRevisionDateEditsByOrderId((prev) => ({
                            ...prev,
                            [activeDraft.orderId]: {
                              ...(prev[activeDraft.orderId] ?? { invoiceDate: '', dueDate: '' }),
                              invoiceDate: nextValue,
                            },
                          }));
                        } else {
                          updateActiveEdits({ invoiceDate: event.target.value });
                        }
                      }}
                    />
                  </div>

                  <div className="invoice-field">
                    <label htmlFor={`invoice-due-${activeDraft.orderId}`}>Due date</label>

                    <input
                      id={`invoice-due-${activeDraft.orderId}`}
                      type="date"
                      value={
                        activeDraft.existingInvoice?.quickBooksInvoiceId
                          ? (revisionDateEditsByOrderId[activeDraft.orderId]?.dueDate ?? '')
                          : activeEdits.dueDate
                      }
                      onChange={(event) => {
                        if (activeDraft.existingInvoice?.quickBooksInvoiceId) {
                          const nextValue = event.target.value;
                          setRevisionDateEditsByOrderId((prev) => ({
                            ...prev,
                            [activeDraft.orderId]: {
                              ...(prev[activeDraft.orderId] ?? { invoiceDate: '', dueDate: '' }),
                              dueDate: nextValue,
                            },
                          }));
                        } else {
                          updateActiveEdits({ dueDate: event.target.value });
                        }
                      }}
                    />
                  </div>

                  <div className="invoice-field">
                    <label>Invoice no.</label>

                    <div className="invoice-field__read-only">
                      {activeDraft.existingInvoice?.quickBooksDocNumber ?? 'Assigned by QuickBooks'}
                    </div>
                  </div>
                </div>
              </section>

              {activeDraft.existingInvoice?.quickBooksInvoiceId && (
                <InvoiceRevisionPanel
                  key={activeDraft.orderId}
                  draft={activeDraft}
                  invoiceDateOverride={
                    revisionDateEditsByOrderId[activeDraft.orderId]?.invoiceDate || null
                  }
                  dueDateOverride={revisionDateEditsByOrderId[activeDraft.orderId]?.dueDate || null}
                  onInvoiceUpdated={onInvoiceCreated}
                  accountingCatalog={accountingQuery.data ?? null}
                  onReload={() => previewQuery.refetch()}
                />
              )}
              <section className="invoice-dialog__section">
                <div className="invoice-dialog__section-heading">
                  <div>
                    <h3>Product / service lines</h3>

                    <p>
                      {isPostedQuickBooksInvoice
                        ? 'These are the actual product and service lines retrieved from QuickBooks. The existing invoice is not recalculated or edited.'
                        : 'BMP automatically separates verified flat direct-ship fees and fuel surcharges into configured QuickBooks Service lines. Other services can be added manually below.'}
                    </p>
                  </div>

                  {!activeDraft.existingInvoice?.quickBooksInvoiceId && (
                    <div className="invoice-dialog__line-actions">
                      {configuredHighwayService && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={createMutation.isPending || invoiceSettingsQuery.isLoading}
                          onClick={() => addServiceLine(configuredHighwayService)}
                        >
                          <Plus size={14} />
                          Add highway use fee
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        disabled={
                          createMutation.isPending ||
                          invoiceSettingsQuery.isLoading ||
                          selectableServiceOptions.length === 0
                        }
                        onClick={() => addServiceLine()}
                      >
                        <Plus size={14} />
                        Add service
                      </button>
                    </div>
                  )}
                </div>

                {showFuelConfigurationWarning && (
                  <div className="invoice-dialog__fuel-config-warning">
                    <AlertTriangle size={17} />

                    <div>
                      <strong>Fuel surcharge service is not configured</strong>

                      <span>
                        This order includes a BMP fuel surcharge, but BMP does not yet know which
                        QuickBooks Service item to use. Go to Integrations → Blume → Invoice
                        settings, select the Fuel Surcharge service, and save before creating this
                        invoice.
                      </span>
                    </div>
                  </div>
                )}

                <div className="invoice-dialog__table-wrap">
                  <table className="invoice-dialog__table">
                    <thead>
                      <tr>
                        <th>Product / service</th>

                        <th>BMP qty</th>

                        <th>QBO item</th>

                        <th className="ta-right">Current QBO qty</th>

                        <th className="ta-right">Rate</th>

                        <th className="ta-right">Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {isPostedQuickBooksInvoice ? (
                        postedQuickBooksLines ? (
                          postedQuickBooksLines.map((line, index) => (
                            <tr key={`${line.id}-${index}`}>
                              <td>
                                <strong>
                                  {line.itemName ?? line.description ?? 'QuickBooks service'}
                                </strong>
                                {line.description && line.description !== line.itemName && (
                                  <small>{line.description}</small>
                                )}
                              </td>
                              <td>—</td>
                              <td>{line.itemName ?? '—'}</td>
                              <td className="ta-right">
                                {line.quantity === null
                                  ? '—'
                                  : new Intl.NumberFormat('en-US', {
                                      maximumFractionDigits: 8,
                                    }).format(line.quantity)}
                              </td>
                              <td className="ta-right">
                                {line.unitPrice === null
                                  ? '—'
                                  : new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      maximumFractionDigits: 6,
                                    }).format(line.unitPrice)}
                              </td>
                              <td className="ta-right">
                                <strong>{formatCents(line.amountCents)}</strong>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6}>
                              <span className="invoice-dialog__missing">
                                QuickBooks line details are temporarily unavailable. Refresh the
                                invoice preview to retry. No amounts have been changed.
                              </span>
                            </td>
                          </tr>
                        )
                      ) : (
                        activeDraft.lines.map((line) => (
                          <Fragment key={line.orderItemId}>
                            <tr>
                              <td>
                                <strong>{line.productName}</strong>

                                <small>
                                  {[line.loadNumber, line.ticketNumber]
                                    .filter(Boolean)
                                    .join(' · ') || '—'}
                                </small>

                                {activeDraft.existingInvoice?.quickBooksInvoiceId &&
                                  (line.directShipFeeCents ?? 0) > 0 && (
                                    <small className="invoice-dialog__line-note">
                                      BMP saved direct ship fee:{' '}
                                      {formatCents(line.directShipFeeCents)}. Check QBO for the
                                      actual posted service line.
                                    </small>
                                  )}

                                {line.currentQuickBooksLine &&
                                  !line.currentQuickBooksLine.usesActualQuantity && (
                                    <small className="invoice-dialog__line-note">
                                      {activeDraft.existingInvoice?.quickBooksInvoiceId
                                        ? 'Saved BMP preview uses Qty 1; view the actual invoice in QuickBooks to verify its lines.'
                                        : 'Exact-total fallback: QBO will use Qty 1 because the saved product amount cannot be represented exactly with a cents-based per-unit rate.'}
                                    </small>
                                  )}
                              </td>

                              <td>
                                {formatQuantity(line.bmpQuantity)} {line.unitName ?? ''}
                              </td>

                              <td>
                                {line.quickBooksItemName ?? (
                                  <span className="invoice-dialog__missing">Not mapped</span>
                                )}
                              </td>

                              <td className="ta-right">
                                {line.currentQuickBooksLine
                                  ? formatQuantity(line.currentQuickBooksLine.quantity)
                                  : '—'}
                              </td>

                              <td className="ta-right">
                                {line.currentQuickBooksLine
                                  ? formatCents(line.currentQuickBooksLine.unitPriceCents)
                                  : '—'}
                              </td>

                              <td className="ta-right">
                                <strong>{formatCents(line.productSubtotalCents)}</strong>
                              </td>
                            </tr>

                            {line.automaticDeliveryLine && (
                              <tr className="invoice-dialog__auto-service-row">
                                <td>
                                  <div className="invoice-dialog__auto-service-title">
                                    <strong>Delivery (per ton)</strong>
                                    <span>Auto from BMP</span>
                                  </div>
                                  <small>
                                    {line.loadNumber
                                      ? `Load ${line.loadNumber}`
                                      : 'Per-ton delivery charge'}
                                  </small>
                                </td>
                                <td>—</td>
                                <td>
                                  {line.automaticDeliveryLine.quickBooksItemName ?? (
                                    <span className="invoice-dialog__missing">
                                      Delivery Service not configured
                                    </span>
                                  )}
                                </td>
                                <td className="ta-right">
                                  {formatQuantity(line.automaticDeliveryLine.quantity)}
                                </td>
                                <td className="ta-right">
                                  {formatCents(line.automaticDeliveryLine.unitPriceCents)}
                                </td>
                                <td className="ta-right">
                                  <strong>
                                    {formatCents(line.automaticDeliveryLine.amountCents)}
                                  </strong>
                                </td>
                              </tr>
                            )}

                            {line.automaticDirectShipLine && (
                              <tr className="invoice-dialog__auto-service-row">
                                <td>
                                  <div className="invoice-dialog__auto-service-title">
                                    <strong>Direct ship fee</strong>
                                    <span>Auto from BMP</span>
                                  </div>
                                  <small>
                                    {line.loadNumber
                                      ? `Load ${line.loadNumber}`
                                      : 'Flat carrier charge'}
                                  </small>
                                </td>
                                <td>—</td>
                                <td>
                                  {line.automaticDirectShipLine.quickBooksItemName ?? (
                                    <span className="invoice-dialog__missing">
                                      Direct ship service not configured
                                    </span>
                                  )}
                                </td>
                                <td className="ta-right">1</td>
                                <td className="ta-right">
                                  {formatCents(line.automaticDirectShipLine.unitPriceCents)}
                                </td>
                                <td className="ta-right">
                                  <strong>
                                    {formatCents(line.automaticDirectShipLine.amountCents)}
                                  </strong>
                                </td>
                              </tr>
                            )}

                            {(line.automaticFuelLines ?? []).map((fuelLine) => (
                              <tr
                                className="invoice-dialog__auto-service-row"
                                key={`${line.orderItemId}-fuel-${fuelLine.kind}`}
                              >
                                <td>
                                  <div className="invoice-dialog__auto-service-title">
                                    <strong>{fuelLine.description}</strong>
                                    <span>Auto from BMP</span>
                                  </div>
                                </td>
                                <td>—</td>
                                <td>
                                  {fuelLine.quickBooksItemName ?? (
                                    <span className="invoice-dialog__missing">
                                      Fuel service not configured
                                    </span>
                                  )}
                                </td>
                                <td className="ta-right">
                                  {new Intl.NumberFormat('en-US', {
                                    maximumFractionDigits: 6,
                                  }).format(fuelLine.quantity)}
                                </td>
                                <td className="ta-right">{formatCents(fuelLine.unitPriceCents)}</td>
                                <td className="ta-right">
                                  <strong>{formatCents(fuelLine.amountCents)}</strong>
                                </td>
                              </tr>
                            ))}
                            {/* Older preview responses use one aggregate fuel line. */}
                            {line.automaticFuelLines === undefined && line.automaticFuelLine && (
                              <tr className="invoice-dialog__auto-service-row">
                                <td>
                                  <strong>Fuel surcharge</strong>
                                </td>
                                <td>—</td>
                                <td>
                                  {line.automaticFuelLine.quickBooksItemName ??
                                    'Fuel service not configured'}
                                </td>
                                <td className="ta-right">
                                  {formatQuantity(line.automaticFuelLine.quantity)}
                                </td>
                                <td className="ta-right">
                                  {formatCents(line.automaticFuelLine.unitPriceCents)}
                                </td>
                                <td className="ta-right">
                                  <strong>{formatCents(line.automaticFuelLine.amountCents)}</strong>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))
                      )}
                    </tbody>

                    <tfoot>
                      <tr>
                        <td colSpan={5}>Total</td>

                        <td className="ta-right">
                          <strong>{formatCents(activeDraft.totalAmountCents)}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {invoiceSettingsQuery.error && (
                  <div className="invoice-dialog__service-warning">
                    <AlertTriangle size={15} />

                    <span>
                      QuickBooks invoice service settings could not be loaded. If this order
                      includes a fuel surcharge, invoice creation may be blocked until the
                      configured Fuel Surcharge service can be loaded.
                    </span>
                  </div>
                )}

                {!isPostedQuickBooksInvoice && activeEdits.additionalServiceLines.length > 0 && (
                  <div className="invoice-dialog__service-lines">
                    <div className="invoice-dialog__service-lines-heading">
                      <div>
                        <strong>Additional services</strong>

                        <small>
                          These optional lines are added on top of the BMP product, direct-ship fee,
                          and automatic fuel total.
                        </small>
                      </div>
                    </div>

                    {activeEdits.additionalServiceLines.map((line, index) => {
                      const option = serviceOptionById.get(line.quickBooksItemId);

                      const amountCents = additionalServiceAmountCents(line);

                      return (
                        <div className="invoice-dialog__service-line" key={line.id}>
                          <div className="invoice-field invoice-field--service">
                            <label htmlFor={`invoice-service-${activeDraft.orderId}-${line.id}`}>
                              Service {index + 1}
                            </label>

                            <SearchableQuickBooksServiceSelect
                              id={`invoice-service-${activeDraft.orderId}-${line.id}`}
                              value={line.quickBooksItemId}
                              options={selectableServiceOptions.map((service) => ({
                                value: service.quickBooksItemId,
                                label: `${serviceLabel(service)}${
                                  configuredHighwayService?.quickBooksItemId ===
                                  service.quickBooksItemId
                                    ? ' — Highway use fee'
                                    : ''
                                }`,
                                description: service.description ?? null,
                              }))}
                              disabled={
                                createMutation.isPending ||
                                Boolean(activeDraft.existingInvoice?.quickBooksInvoiceId)
                              }
                              onChange={(serviceId) => selectServiceForLine(line, serviceId)}
                            />
                          </div>

                          <div className="invoice-field invoice-field--service-description">
                            <label
                              htmlFor={`invoice-service-description-${activeDraft.orderId}-${line.id}`}
                            >
                              Description
                            </label>

                            <input
                              id={`invoice-service-description-${activeDraft.orderId}-${line.id}`}
                              type="text"
                              value={line.description}
                              disabled={
                                createMutation.isPending ||
                                Boolean(activeDraft.existingInvoice?.quickBooksInvoiceId)
                              }
                              onChange={(event) =>
                                updateServiceLine(line.id, {
                                  description: event.target.value,
                                })
                              }
                              placeholder={
                                option?.description ?? 'Optional QuickBooks line description'
                              }
                            />
                          </div>

                          <div className="invoice-field invoice-field--service-number">
                            <label
                              htmlFor={`invoice-service-qty-${activeDraft.orderId}-${line.id}`}
                            >
                              Qty
                            </label>

                            <input
                              id={`invoice-service-qty-${activeDraft.orderId}-${line.id}`}
                              type="number"
                              min="0.001"
                              step="0.001"
                              value={line.quantity}
                              disabled={
                                createMutation.isPending ||
                                Boolean(activeDraft.existingInvoice?.quickBooksInvoiceId)
                              }
                              onChange={(event) =>
                                updateServiceLine(line.id, {
                                  quantity: event.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="invoice-field invoice-field--service-number">
                            <label
                              htmlFor={`invoice-service-rate-${activeDraft.orderId}-${line.id}`}
                            >
                              Rate
                            </label>

                            <input
                              id={`invoice-service-rate-${activeDraft.orderId}-${line.id}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.unitPrice}
                              disabled={
                                createMutation.isPending ||
                                Boolean(activeDraft.existingInvoice?.quickBooksInvoiceId)
                              }
                              onChange={(event) =>
                                updateServiceLine(line.id, {
                                  unitPrice: event.target.value,
                                })
                              }
                              placeholder="0.00"
                            />
                          </div>

                          <div className="invoice-dialog__service-amount">
                            <span>Amount</span>

                            <strong>{formatCents(amountCents)}</strong>
                          </div>

                          <button
                            type="button"
                            className="invoice-dialog__remove-service"
                            title="Remove service line"
                            aria-label={`Remove service line ${index + 1}`}
                            disabled={
                              createMutation.isPending ||
                              Boolean(activeDraft.existingInvoice?.quickBooksInvoiceId)
                            }
                            onClick={() => removeServiceLine(line.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="invoice-dialog__invoice-total">
                  <span>
                    {isPostedQuickBooksInvoice
                      ? 'QuickBooks invoice total'
                      : 'Invoice total with additional services'}
                  </span>

                  <strong>{formatCents(displayedInvoiceTotalCents)}</strong>
                </div>

                <div className="invoice-dialog__payload-note">
                  <FileText size={16} />

                  <span>
                    {showFuelConfigurationWarning
                      ? 'BMP found a saved fuel surcharge on this order, but the QuickBooks Fuel Surcharge service has not been configured yet. Configure it in Integrations → Blume → Invoice settings before creating the invoice.'
                      : 'Fuel surcharge and saved direct-ship fees are automatically split into their configured QuickBooks Services when pricing can be reconciled. Highway Use Fee and other one-off services remain editable before invoice creation.'}
                  </span>
                </div>
              </section>

              <section className="invoice-dialog__section invoice-dialog__section--notes">
                <div className="invoice-dialog__grid invoice-dialog__grid--2">
                  <div className="invoice-field">
                    <label htmlFor={`invoice-customer-memo-${activeDraft.orderId}`}>
                      Customer memo
                    </label>

                    <textarea
                      id={`invoice-customer-memo-${activeDraft.orderId}`}
                      rows={3}
                      value={activeEdits.customerMemo}
                      onChange={(event) =>
                        updateActiveEdits({
                          customerMemo: event.target.value,
                        })
                      }
                      placeholder="Optional memo visible on the invoice"
                    />
                  </div>

                  <div className="invoice-field">
                    <label htmlFor={`invoice-private-note-${activeDraft.orderId}`}>
                      Private note
                    </label>

                    <textarea
                      id={`invoice-private-note-${activeDraft.orderId}`}
                      rows={3}
                      value={activeEdits.privateNote}
                      onChange={(event) =>
                        updateActiveEdits({
                          privateNote: event.target.value,
                        })
                      }
                      placeholder="Optional internal QuickBooks note"
                    />
                  </div>
                </div>
              </section>

              <IssueList issues={activeDraft.issues} severity="WARNING" />
            </>
          )}
        </div>

        <footer className="invoice-dialog__footer">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={createMutation.isPending || isAttachingAfterCreate}
          >
            Close
          </button>

          {activeDraft && (
            <div className="invoice-dialog__footer-actions">
              {activeDraft.canCreate && (
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={createMutation.isPending || hasInvalidAdditionalServiceLines}
                  onClick={() => handleCreate('CREATE')}
                >
                  <FileText size={16} />

                  {createMutation.isPending ? 'Creating…' : 'Create in QuickBooks'}
                </button>
              )}

              {activeDraft.canCreateAndSend && (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={createMutation.isPending || isAttachingAfterCreate}
                  onClick={() => handleCreate('CREATE_AND_SEND')}
                >
                  {activeDraft.canSendExisting ? <Mail size={16} /> : <Send size={16} />}

                  {createMutation.isPending
                    ? 'Working…'
                    : activeDraft.canSendExisting
                      ? (selectedDocumentsByOrder[activeDraft.orderId]?.length ?? 0) > 0
                        ? 'Attach & send existing'
                        : 'Send existing invoice'
                      : (selectedDocumentsByOrder[activeDraft.orderId]?.length ?? 0) > 0
                        ? 'Create, attach & send'
                        : 'Create & send'}
                </button>
              )}
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
